import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, Check, ChevronRight, Circle, CircleAlert, CircleHelp, Info, ShieldCheck, TriangleAlert, X } from 'lucide-react';
import { loadLaboratoryAcceptance, recordLaboratoryAcceptance } from './api';
import { CategoryGlyph, CheckGlyph, SpecimenGlyph } from './LaboratoryGlyphs';
import { LabResource, TubeState, labDay, labError, labWhen } from './LaboratoryParts';
import { useLabScan } from './labScanContext';
import {
  categoryName, criterionText, departmentShort, itemLabel, labAmount, labTemperature, ownSampleTypeName, sampleTypeName, unitSymbol,
} from './LaboratoryText';
import { Pill, XomicsSelect } from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './laboratory-receipt.css';

const text = (key, values) => tx(`ui.lab.accept.${key}`, values);
const CHECKS = ['labels', 'container', 'clarity', 'temperature', 'amount'];
const AMOUNT_DIMENSIONS = ['volume', 'mass', 'cell_count', 'cell_density'];
// A rule is shown with the check it is about; sample information is checked with the labels.
const RULE_CHECK = { information: 'labels', container: 'container', temperature: 'temperature', amount: 'amount' };
const RULE_ICONS = { accept: Check, reject: X, note: Info };
const DECISION_LOOK = { accepted: [Check, 'ok'], accepted_conditional: [CircleAlert, 'warn'], quarantined: [Ban, 'bad'], on_hold: [CircleHelp, 'warn'] };
const today = () => new Date().toISOString().slice(0, 10);
const entered = value => (value === '' || !Number.isFinite(Number(value)) ? null : Number(value));
const ruleCount = count => tx(count === 1 ? 'ui.lab.rules.rule_one' : 'ui.lab.rules.rule_count', { count });

// The numbers the rules put on this kind of tube, as the server compares them.
export function acceptanceLimits(option) {
  if (option === null) return { amounts: [], analyses: [], aliquots: [], temperatures: [] };
  return {
    amounts: option.criteria.filter(k => k.amountMin !== null).map(k => ({ min: k.amountMin, max: k.amountMax, unit: k.amountUnit })),
    analyses: option.criteria.some(k => k.usesAnalysisList) ? option.analysisRequirements.flatMap(entry => entry.requirements
      .filter(r => AMOUNT_DIMENSIONS.includes(r.parameter) && ['minimum', 'range', 'requested'].includes(r.qualifier) && r.valueMin !== null && r.unit !== null)
      .map(r => ({ analysisCode: entry.analysisCode, min: r.valueMin, max: r.valueMax, unit: r.unit }))) : [],
    aliquots: option.criteria.filter(k => k.aliquotsMin !== null).map(k => k.aliquotsMin),
    temperatures: option.criteria.filter(k => k.temperatureMaxC !== null).map(k => k.temperatureMaxC),
  };
}

// What the entered numbers say against those limits. A crossed limit makes its
// check No and rules out plain acceptance, exactly as the server decides it.
export function acceptanceReadings(limits, values, units) {
  const unit = units.find(u => u.code === values.unitCode) || null;
  const amount = entered(values.amount), aliquots = entered(values.aliquots), temperature = entered(values.temperature);
  const low = limit => amount !== null && unit !== null && unit.dimension === limit.unit.dimension && amount * unit.factor / limit.unit.factor < limit.min;
  const amountsLow = limits.amounts.filter(low), analysesLow = limits.analyses.filter(low);
  const aliquotsLow = aliquots === null ? [] : limits.aliquots.filter(min => aliquots < min);
  const temperaturesHigh = temperature === null ? [] : limits.temperatures.filter(max => temperature > max);
  return {
    amount, aliquots, temperature, amountsLow, analysesLow, aliquotsLow, temperaturesHigh,
    amountRequired: limits.amounts.length + limits.analyses.length > 0, aliquotsRequired: limits.aliquots.length > 0,
    amountCrossed: amountsLow.length + analysesLow.length + aliquotsLow.length > 0, temperatureCrossed: temperaturesHigh.length > 0,
  };
}

// What is still missing before the acceptance can be recorded, in the order of the form.
export function acceptanceNeeds({ sideMissing, option, checks, issues, readings, decision, criterion, waiver, discussion, note }) {
  const needs = [];
  if (sideMissing) needs.push({ code: 'side' });
  if (option === null) needs.push({ code: 'sample_type' });
  const open = CHECKS.filter(check => checks[check] === null).length;
  if (open > 0) needs.push({ code: 'checks', count: open });
  if (['container', 'clarity'].some(check => checks[check] === false && issues[check].length === 0)) needs.push({ code: 'reason' });
  if (checks.temperature === false && readings.temperature === null) needs.push({ code: 'temperature' });
  if (readings.amountRequired && readings.amount === null) needs.push({ code: 'amount' });
  if (readings.aliquotsRequired && readings.aliquots === null) needs.push({ code: 'aliquots' });
  if (decision === null) return [...needs, { code: 'decision' }];
  if (decision.requiresCriterion && criterion === null) needs.push({ code: 'condition' });
  if (decision.requiresCriterion && criterion !== null && criterion.requiresWaiver
    && (waiver.reference.trim() === '' || waiver.approvedByName.trim() === '' || waiver.approvedOn === '')) needs.push({ code: 'waiver' });
  if (decision.requiresCriterion && criterion !== null && criterion.requiresDiscussionRecord && discussion.trim() === '') needs.push({ code: 'discussion' });
  if (decision.requiresNote && note.trim() === '') needs.push({ code: 'note' });
  return needs;
}

const NEED_TEXT = {
  side: 'ui.lab.accept.need.side', sample_type: 'ui.lab.accept.need.sample_type', reason: 'ui.lab.error.LAB_ACCEPTANCE_REASON_REQUIRED',
  temperature: 'ui.lab.error.LAB_ACCEPTANCE_TEMPERATURE_REQUIRED', amount: 'ui.lab.accept.need.amount',
  aliquots: 'ui.lab.error.LAB_ACCEPTANCE_ALIQUOTS_REQUIRED', decision: 'ui.lab.accept.need.decision',
  condition: 'ui.lab.error.LAB_ACCEPTANCE_CONDITION_REQUIRED', waiver: 'ui.lab.error.LAB_ACCEPTANCE_WAIVER_REQUIRED',
  discussion: 'ui.lab.error.LAB_ACCEPTANCE_DISCUSSION_REQUIRED', note: 'ui.lab.error.LAB_ACCEPTANCE_NOTE_REQUIRED',
};
const needText = need => (need.code === 'checks' ? text(need.count === 1 ? 'need.check_one' : 'need.checks', { count: need.count }) : tx(NEED_TEXT[need.code]));

const Required = () => <em className="xo-accept-required">{tx('ui.lab.value.required')}</em>;

function YesNo({ value, onChange, label, disabled, forced }) {
  return <div className="xo-accept-yesno" role="radiogroup" aria-label={label}>
    {[[true, 'yes'], [false, 'no']].map(([option, key]) => <button type="button" role="radio" key={key} aria-checked={value === option}
      className={value === option ? `is-active is-${key}` : ''} disabled={disabled || (forced !== null && forced !== option)} onClick={() => onChange(option)}>
      {option ? <Check aria-hidden="true" /> : <X aria-hidden="true" />}{tx(`ui.lab.${key}`)}
    </button>)}
  </div>;
}

function Chips({ items, selected, onToggle, label, disabled }) {
  return <div className="xo-accept-field"><span>{label}{selected.length === 0 && <Required />}</span>
    <div className="xo-accept-chips" role="group" aria-label={label}>
      {items.filter(item => item.isActive).map(item => <button type="button" key={item.code} aria-pressed={selected.includes(item.code)}
        className={selected.includes(item.code) ? 'is-active' : ''} disabled={disabled} onClick={() => onToggle(item.code)}>{itemLabel(item)}</button>)}
    </div></div>;
}

// One figure of the rules beside the number it limits: neutral until a number
// is entered, then met or crossed.
function Limit({ state, children }) {
  return <li className={`is-${state}`}>{state === 'met' ? <Check aria-hidden="true" /> : state === 'crossed' ? <X aria-hidden="true" /> : <Circle aria-hidden="true" />}
    <span>{children}</span></li>;
}

const amountLimitLabel = limit => (limit.max === null ? `${tx('ui.lab.limit.minimum')} ${labAmount(limit.min, null, limit.unit)}`
  : `${tx('ui.lab.limit.amount')} ${labAmount(limit.min, limit.max, limit.unit)}`);

// One check: what it is, whether a No blocks plain acceptance, the answer, what
// goes with the answer, and the rules it stands for, a click away.
function CheckRow({ check, blocking, value, forced, rules, ruleSetCode, disabled, onChange, children }) {
  const [open, setOpen] = useState(false);
  return <div className={`xo-accept-check${value === null ? '' : value ? ' is-yes' : ' is-no'}`}>
    <span className="xo-lab-glyph"><CheckGlyph code={check} /></span>
    <div className="xo-accept-check-title"><strong>{text(`check.${check}`)}</strong>
      <small>{tx(blocking ? 'ui.lab.rules.check_required' : 'ui.lab.rules.check_noted')}</small></div>
    <YesNo value={value} forced={forced} label={text(`check.${check}`)} disabled={disabled} onChange={onChange} />
    {(children || rules.length > 0) && <div className="xo-accept-check-body">
      {children}
      {forced !== null && <p className="xo-accept-forced"><TriangleAlert aria-hidden="true" />{text('limit_crossed')}</p>}
      {rules.length > 0 && <RuleList rules={rules} ruleSetCode={ruleSetCode} open={open} onToggle={() => setOpen(!open)} />}
    </div>}
  </div>;
}

function RuleList({ rules, ruleSetCode, open, onToggle }) {
  return <>
    <button type="button" className="xo-accept-rules-toggle" aria-expanded={open} onClick={onToggle}><ChevronRight aria-hidden="true" />{ruleCount(rules.length)}</button>
    {open && <ul className="xo-accept-rules">{rules.map(rule => {
      const Icon = RULE_ICONS[rule.kind];
      return <li key={rule.id} className={`is-${rule.kind}`}><span><Icon aria-hidden="true" /></span>{criterionText(ruleSetCode, rule.position)}</li>;
    })}</ul>}
  </>;
}

// The rules of the tube's sample type at a glance: its section and the numbers in it.
function RuleFigures({ ruleSet, option, limits }) {
  const figures = [
    ...limits.amounts.map(limit => [tx(limit.max === null ? 'ui.lab.limit.minimum' : 'ui.lab.limit.amount'), labAmount(limit.min, limit.max, limit.unit)]),
    ...limits.aliquots.map(min => [tx('ui.lab.limit.aliquots'), tx('ui.lab.req.qualifier.minimum', { value: min })]),
    ...limits.temperatures.map(max => [tx('ui.lab.limit.warmest'), labTemperature(max)]),
  ].filter((figure, index, all) => all.findIndex(other => other[0] === figure[0] && other[1] === figure[1]) === index);
  return <div className="xo-accept-summary">
    {option.section !== null && <span className="xo-lab-glyph is-large"><CategoryGlyph code={option.section.code} /></span>}
    <div className="xo-accept-summary-title">
      <strong>{option.section === null ? sampleTypeName(option.code) : categoryName(option.section.code)}</strong>
      <Link to={`/xomics/laboratory/acceptance-rules?lab=${ruleSet.department.code}${option.section === null ? '' : `&sample=${option.section.code}`}`}>
        {text('rules_of', { department: departmentShort(ruleSet.department.code) })}<ChevronRight aria-hidden="true" /></Link>
    </div>
    {figures.length > 0 && <div className="xo-rule-figures">{figures.map(([label, value]) => <div key={`${label}-${value}`}><small>{label}</small><strong>{value}</strong></div>)}</div>}
  </div>;
}

function Attempt({ attempt }) {
  return <li>
    <div><strong>{text('attempt', { number: attempt.attemptNo })}</strong><TubeState value={attempt.decision.tubeState} />
      {attempt.status !== 'completed' && <Pill tone="warn">{tx(`ui.lab.stage.status.${attempt.status}`)}</Pill>}</div>
    <p>{text('performed', { name: attempt.performedBy, at: labWhen(attempt.performedAt) })} · {sampleTypeName(attempt.sampleType.code)}</p>
    <p className="xo-accept-check-summary">{CHECKS.map(check => <span key={check} className={attempt.checks[check] ? 'is-yes' : 'is-no'}>
      {attempt.checks[check] ? <Check aria-hidden="true" /> : <X aria-hidden="true" />}{text(`check.${check}`)}</span>)}</p>
    {attempt.issues.length > 0 && <p>{attempt.issues.map(issue => itemLabel(issue)).join(', ')}</p>}
    {attempt.observedAmount && <p>{text('observed', { value: attempt.observedAmount.value, unit: attempt.observedAmount.unit })}</p>}
    {attempt.warnings.filter(w => w.code !== 'no_section').map((warning, index) => <p key={index} className="xo-accept-warning">
      <TriangleAlert aria-hidden="true" />{text(`warning.${warning.code}`, { limit: warning.limit ? `${warning.limit.value} ${warning.limit.unit || ''}`.trim() : '' })}</p>)}
    {attempt.condition && <p>{criterionText(attempt.condition.ruleSetCode, attempt.condition.position)}{attempt.condition.waiverReference && ` · ${attempt.condition.waiverFormCode || ''} ${attempt.condition.waiverReference} · ${attempt.condition.waiverApprovedByName} · ${labDay(attempt.condition.waiverApprovedOn)}`}</p>}
    {attempt.note && <blockquote>{attempt.note}</blockquote>}
    {attempt.reviewedAt !== null && <p>{text('reviewed', { name: attempt.reviewedBy, at: labWhen(attempt.reviewedAt) })}</p>}
    {attempt.reviewNote && <blockquote>{attempt.reviewNote}</blockquote>}
  </li>;
}

// What was recorded for the tube. The latest attempt stands open when it is the
// answer on screen; with the form above it, the attempts fold away.
function Attempts({ attempts, open }) {
  if (attempts.length === 0) return null;
  const latest = attempts[attempts.length - 1], earlier = attempts.slice(0, -1);
  if (open) {
    return <div className="xo-accept-history"><ol><Attempt attempt={latest} /></ol>
      {earlier.length > 0 && <details><summary><ChevronRight aria-hidden="true" />{text('history', { count: earlier.length })}</summary>
        <ol>{earlier.map(attempt => <Attempt key={attempt.uuid} attempt={attempt} />)}</ol></details>}</div>;
  }
  return <div className="xo-accept-history"><details><summary><ChevronRight aria-hidden="true" />{text('history', { count: attempts.length })}</summary>
    <ol>{attempts.map(attempt => <Attempt key={attempt.uuid} attempt={attempt} />)}</ol></details></div>;
}

// A tube made as a sample type alone, of a kind both sides of the laboratory
// take: the laboratory says which side, and that side's rules apply.
export function sidesToChoose(data) {
  return data.bySampleType === true && Array.isArray(data.departments) && data.departments.length > 1;
}

function Sides({ departments, value, disabled, onChange }) {
  return <section className="xo-accept-section">
    <h4>{text('side')}{value === null && <Required />}</h4>
    <div className="xo-accept-options" role="radiogroup" aria-label={text('side')}>
      {departments.map(department => <button type="button" role="radio" key={department.code} aria-checked={value === department.code}
        className={value === department.code ? 'is-active' : ''} onClick={() => onChange(department.code)} disabled={disabled}>
        <span><strong>{departmentShort(department.code)}</strong><small>{department.analyses.join(' · ')}</small></span></button>)}
    </div>
  </section>;
}

// The kind the form starts on: the one the tube was made as, or the only one offered.
export function expectedSampleType(sampleTypes) {
  const expected = sampleTypes.find(type => type.expected === true);
  return expected !== undefined ? expected.code : sampleTypes.length === 1 ? sampleTypes[0].code : '';
}

function AcceptanceForm({ data, onRecorded }) {
  // The tube starts as the kind it was made as; the laboratory changes it when the tube is another kind.
  const [sampleTypeCode, setSampleTypeCode] = useState(expectedSampleType(data.sampleTypes));
  const [answers, setAnswers] = useState({ labels: null, container: null, clarity: null, temperature: null, amount: null });
  const [issues, setIssues] = useState({ container: [], clarity: [] });
  const [temperature, setTemperature] = useState(''), [amount, setAmount] = useState(''), [unit, setUnit] = useState('');
  const [aliquots, setAliquots] = useState(''), [containerType, setContainerType] = useState(''), [theirCode, setTheirCode] = useState('');
  const [decisionCode, setDecisionCode] = useState(''), [criterionId, setCriterionId] = useState('');
  const [waiver, setWaiver] = useState({ reference: '', approvedByName: '', approvedOn: today() });
  const [discussion, setDiscussion] = useState(''), [note, setNote] = useState('');
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [openOther, setOpenOther] = useState(false);
  const retry = useRef(null), inFlight = useRef(false);
  // Answers given and not yet recorded: a scan of another tube asks before it moves on.
  useLabScan({ tubeCode: data.tube.code, tubeLabel: data.tube.printedCode ?? data.tube.code,
    dirty: Object.values(answers).some(answer => answer !== null) || amount !== '' || temperature !== '' || decisionCode !== '' || note.trim() !== '' });

  const option = data.sampleTypes.find(type => type.code === sampleTypeCode) || null;
  const limits = useMemo(() => acceptanceLimits(option), [option]);
  // The amount is entered in a unit the rules use, starting on the unit of their first figure.
  const measured = [...limits.amounts, ...limits.analyses];
  const units = data.units.filter(u => (measured.length ? measured.some(limit => limit.unit.dimension === u.dimension) : AMOUNT_DIMENSIONS.includes(u.dimension)));
  const unitCode = units.some(u => u.code === unit) ? unit : measured.length ? measured[0].unit.code : units.length ? units[0].code : '';
  const amountUnit = units.find(u => u.code === unitCode) || null;
  const readings = acceptanceReadings(limits, { amount, unitCode, aliquots, temperature }, data.units);
  const amountState = (limit, low) => (readings.amount === null || amountUnit === null || amountUnit.dimension !== limit.unit.dimension ? 'open'
    : low.includes(limit) ? 'crossed' : 'met');
  // A crossed limit answers its own check.
  const forced = { amount: readings.amountCrossed ? false : null, temperature: readings.temperatureCrossed ? false : null };
  const checks = { ...answers, ...(forced.amount === null ? {} : { amount: false }), ...(forced.temperature === null ? {} : { temperature: false }) };
  const blocking = data.ruleSet.checks.filter(check => check.blocksPlainAccept).map(check => check.code);
  const acceptBlocked = blocking.some(check => checks[check] === false) || readings.amountCrossed || readings.temperatureCrossed;
  const conditions = data.ruleSet.criteria.filter(k => k.kind === 'conditional' && !k.refuses);
  const refusals = data.ruleSet.criteria.filter(k => k.refuses);
  const chosen = data.ruleSet.decisions.find(d => d.code === decisionCode) || null;
  const decision = chosen !== null && chosen.code === 'accept' && acceptBlocked ? null : chosen;
  const criterion = decision !== null && decision.requiresCriterion ? conditions.find(k => String(k.id) === criterionId) || null : null;
  const rulesOf = check => (option === null ? [] : option.criteria.filter(k => k.kind !== 'conditional' && RULE_CHECK[k.check] === check));
  const otherRules = option === null ? [] : option.criteria.filter(k => k.kind !== 'conditional' && RULE_CHECK[k.check] === undefined);
  const needs = acceptanceNeeds({ sideMissing: false, option, checks, issues, readings, decision, criterion, waiver, discussion, note });
  const toggle = (check, code) => setIssues(current => ({ ...current, [check]: current[check].includes(code)
    ? current[check].filter(value => value !== code) : [...current[check], code] }));
  const answer = check => value => setAnswers(current => ({ ...current, [check]: value }));

  async function submit() {
    if (inFlight.current || needs.length > 0) return;
    const body = { sampleUuid: data.tube.uuid, sampleTypeCode, checks,
      issues: { container: checks.container ? [] : issues.container, clarity: checks.clarity ? [] : issues.clarity },
      ...(readings.temperature !== null ? { loggedTemperatureC: readings.temperature } : {}),
      ...(readings.amount !== null ? { observedAmount: { value: readings.amount, unitCode } } : {}),
      ...(readings.aliquots !== null ? { aliquotCount: readings.aliquots } : {}),
      ...(containerType !== '' ? { containerTypeCode: containerType } : {}),
      decisionCode: decision.code,
      ...(criterion !== null ? { conditionalCriterionId: criterion.id } : {}),
      ...(criterion !== null && criterion.requiresWaiver ? { waiver } : {}),
      ...(criterion !== null && criterion.requiresDiscussionRecord ? { discussionRecord: discussion } : {}),
      ...(note.trim() !== '' ? { note } : {}), ...(theirCode.trim() !== '' ? { organizationCode: theirCode } : {}),
      ...(sidesToChoose(data) ? { departmentCode: data.department } : {}) };
    const signature = JSON.stringify(body);
    if (retry.current === null || retry.current.signature !== signature) retry.current = { signature, requestId: crypto.randomUUID() };
    inFlight.current = true; setBusy(true); setError('');
    try {
      const result = await recordLaboratoryAcceptance({ ...body, requestId: retry.current.requestId });
      retry.current = null; onRecorded(result);
    } catch (caught) { setError(labError(caught)); }
    finally { inFlight.current = false; setBusy(false); }
  }

  const limitState = crossed => (crossed ? 'crossed' : 'met');
  return <div className="xo-accept-form">
    <section className="xo-accept-section">
      <h4>{text('sample_type')}{option === null && <Required />}</h4>
      {data.sampleTypes.length > 1 && <div className="xo-accept-options" role="radiogroup" aria-label={text('sample_type')}>
        {data.sampleTypes.map(type => <button type="button" role="radio" key={type.code} aria-checked={sampleTypeCode === type.code}
          className={sampleTypeCode === type.code ? 'is-active' : ''} onClick={() => setSampleTypeCode(type.code)} disabled={busy}>
          <span className="xo-lab-glyph"><SpecimenGlyph code={type.code} /></span>
          <span><strong>{sampleTypeName(type.code)}</strong><small>{type.workflows.join(' · ')}</small></span></button>)}
      </div>}
      {option !== null && (option.section === null && limits.amounts.length + limits.aliquots.length + limits.temperatures.length === 0
        ? <p className="xo-accept-note-line"><Info aria-hidden="true" />{text('no_rules')}</p>
        : <RuleFigures ruleSet={data.ruleSet} option={option} limits={limits} />)}
    </section>

    <section className="xo-accept-section">
      <h4>{text('checks')}</h4>
      <div className="xo-accept-checks">
        <CheckRow check="labels" blocking={blocking.includes('labels')} value={checks.labels} forced={null} rules={rulesOf('labels')}
          ruleSetCode={data.ruleSet.code} disabled={busy} onChange={answer('labels')} />
        <CheckRow check="container" blocking={blocking.includes('container')} value={checks.container} forced={null} rules={rulesOf('container')}
          ruleSetCode={data.ruleSet.code} disabled={busy} onChange={answer('container')}>
          {checks.container === false && <Chips items={data.containerIssues} selected={issues.container} label={text('container_reason')}
            disabled={busy} onToggle={code => toggle('container', code)} />}
        </CheckRow>
        <CheckRow check="clarity" blocking={blocking.includes('clarity')} value={checks.clarity} forced={null} rules={rulesOf('clarity')}
          ruleSetCode={data.ruleSet.code} disabled={busy} onChange={answer('clarity')}>
          {checks.clarity === false && <Chips items={data.clarityIssues} selected={issues.clarity} label={text('clarity_reason')}
            disabled={busy} onToggle={code => toggle('clarity', code)} />}
        </CheckRow>
        <CheckRow check="temperature" blocking={blocking.includes('temperature')} value={checks.temperature} forced={forced.temperature} rules={rulesOf('temperature')}
          ruleSetCode={data.ruleSet.code} disabled={busy} onChange={answer('temperature')}>
          <div className="xo-accept-measure">
            <label className="xo-accept-field"><span>{text('logged_temperature')}{checks.temperature === false && readings.temperature === null && <Required />}</span>
              <span className="xo-accept-number"><input type="number" step="0.1" inputMode="decimal" value={temperature}
                onChange={event => setTemperature(event.target.value)} disabled={busy} /><b>°C</b></span></label>
            {limits.temperatures.length > 0 && <ul className="xo-accept-limits">{limits.temperatures.map(max => <Limit key={max}
              state={readings.temperature === null ? 'open' : limitState(readings.temperature > max)}>{tx('ui.lab.limit.warmest')} {labTemperature(max)}</Limit>)}</ul>}
          </div>
        </CheckRow>
        <CheckRow check="amount" blocking={blocking.includes('amount')} value={checks.amount} forced={forced.amount} rules={rulesOf('amount')}
          ruleSetCode={data.ruleSet.code} disabled={busy} onChange={answer('amount')}>
          <div className="xo-accept-measure">
            <div className="xo-accept-measure-fields">
              <label className="xo-accept-field"><span>{text('observed_amount')}{readings.amountRequired && readings.amount === null && <Required />}</span>
                <span className="xo-accept-number"><input type="number" min="0" step="any" inputMode="decimal" value={amount}
                  onChange={event => setAmount(event.target.value)} disabled={busy} />
                <XomicsSelect value={unitCode} onChange={event => setUnit(event.target.value)} aria-label={text('unit')} disabled={busy}>
                  {units.map(u => <option key={u.code} value={u.code}>{unitSymbol(u)}</option>)}
                </XomicsSelect></span></label>
              {readings.aliquotsRequired && <label className="xo-accept-field"><span>{text('aliquots')}{readings.aliquots === null && <Required />}</span>
                <span className="xo-accept-number"><input type="number" min="1" max="255" step="1" inputMode="numeric" value={aliquots}
                  onChange={event => setAliquots(event.target.value)} disabled={busy} /></span></label>}
            </div>
            {limits.amounts.length + limits.analyses.length + limits.aliquots.length > 0 && <ul className="xo-accept-limits">
              {limits.amounts.map((limit, index) => <Limit key={`amount-${index}`} state={amountState(limit, readings.amountsLow)}>{amountLimitLabel(limit)}</Limit>)}
              {limits.analyses.map((limit, index) => <Limit key={`analysis-${index}`} state={amountState(limit, readings.analysesLow)}>
                <strong>{limit.analysisCode}</strong> {labAmount(limit.min, limit.max, limit.unit)}</Limit>)}
              {limits.aliquots.map(min => <Limit key={`aliquots-${min}`}
                state={readings.aliquots === null ? 'open' : limitState(readings.aliquots < min)}>
                {tx('ui.lab.limit.aliquots')} {tx('ui.lab.req.qualifier.minimum', { value: min })}</Limit>)}
            </ul>}
          </div>
        </CheckRow>
        {otherRules.length > 0 && <div className="xo-accept-check is-plain">
          <span className="xo-lab-glyph"><CheckGlyph code="other" /></span>
          <div className="xo-accept-check-title"><strong>{tx('ui.lab.rules.check.other')}</strong></div>
          <div className="xo-accept-check-body"><RuleList rules={otherRules} ruleSetCode={data.ruleSet.code} open={openOther} onToggle={() => setOpenOther(!openOther)} /></div>
        </div>}
      </div>
    </section>

    {(data.containerTypes || (data.mayRecordOrganizationCode && data.organizationCode === null)) && <section className="xo-accept-section">
      <h4>{tx('ui.lab.flow.optional')}</h4>
      <div className="xo-accept-extra">
        {data.containerTypes && <label className="xo-accept-field"><span>{text('container_type')}</span>
          <XomicsSelect value={containerType} onChange={event => setContainerType(event.target.value)} disabled={busy}>
            <option value="">{text('not_recorded')}</option>
            {data.containerTypes.filter(item => item.isActive).map(item => <option key={item.code} value={item.code}>{itemLabel(item)}</option>)}
          </XomicsSelect></label>}
        {data.mayRecordOrganizationCode && data.organizationCode === null && <label className="xo-accept-field"><span>{text('their_code', { organization: data.organization.name })}</span>
          <input value={theirCode} maxLength={64} onChange={event => setTheirCode(event.target.value)} disabled={busy} /></label>}
      </div>
    </section>}

    <section className="xo-accept-section">
      <h4>{text('decision')}{decision === null && <Required />}</h4>
      <div className="xo-accept-decisions" role="radiogroup" aria-label={text('decision')}>
        {data.ruleSet.decisions.map(item => {
          const [Icon, tone] = DECISION_LOOK[item.tubeState] || [Circle, 'flat'];
          const blocked = item.code === 'accept' && acceptBlocked;
          return <button type="button" role="radio" key={item.code} aria-checked={decision !== null && decision.code === item.code}
            className={`t-${tone}${decision !== null && decision.code === item.code ? ' is-active' : ''}`} onClick={() => setDecisionCode(item.code)} disabled={busy || blocked}>
            <span className="xo-accept-decision-mark"><Icon aria-hidden="true" /></span>
            <span><strong>{text(`decision.${item.code}`)}</strong><small>{tx(`ui.lab.state.${item.tubeState}`)}</small></span></button>;
        })}
      </div>
      {acceptBlocked && <p className="xo-accept-note-line"><TriangleAlert aria-hidden="true" />{tx('ui.lab.error.LAB_ACCEPTANCE_PLAIN_ACCEPT_BLOCKED')}</p>}
      {decision !== null && decision.requiresCriterion && <div className="xo-accept-condition">
        <span>{text('condition')}{criterion === null && <Required />}</span>
        {conditions.map(item => <label key={item.id} className={criterionId === String(item.id) ? 'is-active' : ''}>
          <input type="radio" name="accept-condition" checked={criterionId === String(item.id)} onChange={() => setCriterionId(String(item.id))} disabled={busy} />
          <span>{criterionText(data.ruleSet.code, item.position)}</span></label>)}
        {refusals.map(item => <p key={item.id} className="xo-accept-refusal"><Ban aria-hidden="true" />{criterionText(data.ruleSet.code, item.position)}</p>)}
        {criterion !== null && criterion.requiresWaiver && <div className="xo-accept-waiver">
          <p>{data.ruleSet.waiverFormCode ? text('waiver_form', { form: data.ruleSet.waiverFormCode }) : text('customer_approval')}</p>
          <label className="xo-accept-field"><span>{text('waiver_reference')}{waiver.reference.trim() === '' && <Required />}</span>
            <input value={waiver.reference} maxLength={128} onChange={event => setWaiver({ ...waiver, reference: event.target.value })} disabled={busy} /></label>
          <label className="xo-accept-field"><span>{text('waiver_approver')}{waiver.approvedByName.trim() === '' && <Required />}</span>
            <input value={waiver.approvedByName} maxLength={255} onChange={event => setWaiver({ ...waiver, approvedByName: event.target.value })} disabled={busy} /></label>
          <label className="xo-accept-field"><span>{text('waiver_date')}{waiver.approvedOn === '' && <Required />}</span>
            <input type="date" value={waiver.approvedOn} max={today()} onChange={event => setWaiver({ ...waiver, approvedOn: event.target.value })} disabled={busy} /></label>
        </div>}
        {criterion !== null && criterion.requiresDiscussionRecord && <label className="xo-accept-field"><span>{text('discussion_record')}{discussion.trim() === '' && <Required />}</span>
          <textarea value={discussion} maxLength={2000} rows={3} onChange={event => setDiscussion(event.target.value)} disabled={busy} /></label>}
      </div>}
      <label className="xo-accept-field"><span>{text('note')}{decision !== null && decision.requiresNote && note.trim() === '' && <Required />}</span>
        <textarea value={note} maxLength={2000} rows={2} onChange={event => setNote(event.target.value)} disabled={busy} /></label>
    </section>

    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
    <div className="xo-accept-actions">
      {needs.length === 0 ? <p className="xo-accept-ready"><Check aria-hidden="true" />{text('need.none')}</p>
        : <div className="xo-accept-needs"><strong>{text('need.title')}</strong>
          <ul>{needs.map(need => <li key={need.code}><Circle aria-hidden="true" />{needText(need)}</li>)}</ul></div>}
      <button type="button" className="xo-btn" onClick={submit} disabled={busy || needs.length > 0}>
        {data.ruleSet.approvalRequirement === 'required' ? <ShieldCheck aria-hidden="true" /> : <Check aria-hidden="true" />}
        {text(busy ? 'saving' : data.ruleSet.approvalRequirement === 'required' ? 'submit_approval' : 'record')}</button>
    </div>
  </div>;
}

// The acceptance of one received tube: context, form and history. `embedded`
// leaves the tube's heading to the dialog the form sits in.
export default function LaboratoryAcceptance({ sampleUuid, embedded = false, onRecorded = () => {} }) {
  // The side of the laboratory chosen here, where the tube's kind is taken by both.
  const [side, setSide] = useState('');
  const load = useCallback(() => loadLaboratoryAcceptance(sampleUuid, side), [sampleUuid, side]);
  const resource = useLiveResource(['samples'], load);
  const [notice, setNotice] = useState('');
  const key = useMemo(() => (resource.data ? `${resource.data.tube.uuid}-${resource.data.nextAttemptNo}-${resource.data.department}` : ''), [resource.data]);
  return <LabResource resource={resource}>{data => {
    const choosing = sidesToChoose(data) && (data.canRecord || (data.blocked && data.blocked.code === 'ACCEPTANCE_DEPARTMENT_AMBIGUOUS'));
    const sideMissing = choosing && data.blocked !== null;
    return <section className={`xo-accept${embedded ? ' is-embedded' : ''}`} aria-label={text('title', { code: data.tube.printedCode ?? data.tube.code })}>
      {!embedded && <header className="xo-accept-head">
        <div><span>{text('title_short')}</span><h3><code>{data.tube.printedCode ?? data.tube.code}</code></h3>
          <p>{ownSampleTypeName(data.collection.sampleType)}{data.organization && ` · ${data.organization.name}`}{data.organizationCode && ` · ${data.organizationCode}`}</p></div>
        <TubeState value={data.tube.state} />
      </header>}
      {notice !== '' && <p className="xo-alert t-ok" role="status"><Check aria-hidden="true" />{notice}</p>}
      {choosing && <Sides departments={data.departments} value={data.department} disabled={resource.loading} onChange={setSide} />}
      {sideMissing ? <div className="xo-accept-actions"><div className="xo-accept-needs"><strong>{text('need.title')}</strong>
        <ul><li><Circle aria-hidden="true" />{needText({ code: 'side' })}</li></ul></div></div>
        : data.blocked ? <p className="xo-alert t-warn" role="status">{tx(`ui.lab.error.${data.blocked.code}`)}</p>
          : data.canRecord ? <AcceptanceForm key={key} data={data} onRecorded={result => {
            setNotice(text(`recorded.${result.tube.state}`)); onRecorded(result); resource.refresh();
          }} /> : data.attempts.length === 0 && <p className="xo-accept-note-line"><Info aria-hidden="true" />{tx(`ui.lab.error.${data.reason}`)}</p>}
      <Attempts attempts={data.attempts} open={!data.canRecord} />
    </section>;
  }}</LabResource>;
}
